import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "../state/authStore";

type Mode = "signin" | "register";

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login, register: registerUser, loading, error } = useAuthStore();

  // When the desktop agent opens this page it passes a desktop_state. We then
  // hand the session back to the waiting agent instead of navigating into the
  // app, and show a "return to the app" confirmation.
  const desktopState = searchParams.get("desktop_state");
  const desktopMode = !!desktopState;
  const [desktopDone, setDesktopDone] = useState(false);

  // The landing-page trial CTA links here with ?trial=1 — open on the
  // create-account tab so a cold visitor isn't dropped on a bare sign-in form.
  const [mode, setMode] = useState<Mode>(searchParams.get("trial") ? "register" : "signin");
  const [localError, setLocalError] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState("");
  const [iracingCustId, setIracingCustId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (!user) return;
    if (desktopMode) return; // desktop flow shows a confirmation, never redirects
    navigate(searchParams.get("returnTo") || "/", { replace: true });
  }, [user, desktopMode, navigate, searchParams]);

  // Desktop "sign in with web" is disabled server-side until the #244/#287
  // user_code binding lands (the un-bound desktop_state instant-authorize was a
  // 1-click ATO). No silent authorize-on-load: opening /login?desktop_state=X
  // just shows the normal sign-in form.

  useEffect(() => {
    setLocalError(null);
    useAuthStore.setState({ error: null });
  }, [mode]);

  const displayError = localError || error || searchParams.get("error");

  const helperCopy = useMemo(() => {
    if (desktopMode && mode === "signin") {
      return "Sign in to connect the StintSense desktop app to your team.";
    }
    return mode === "signin"
      ? "Sign in with the email and password tied to your team account."
      : "Create your account using the invite code from your team admin.";
  }, [mode, desktopMode]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLocalError(null);
    useAuthStore.setState({ error: null });

    if (mode === "register") {
      if (!inviteCode.trim()) return setLocalError("Invite code is required.");
      if (!displayName.trim()) return setLocalError("Full name is required.");
      if (!email.trim() || !email.includes("@")) return setLocalError("A valid email is required.");
      if (!iracingCustId.trim()) return setLocalError("iRacing Customer ID is required.");
      if (password.trim().length < 6) return setLocalError("Password must be at least 6 characters.");

      try {
        await registerUser(inviteCode.trim(), iracingCustId.trim(), displayName.trim(), email.trim(), password);
      } catch {
        return;
      }

      return;
    }

    if (!email.trim()) return setLocalError("Email is required.");
    if (!password) return setLocalError("Password is required.");

    try {
      await login(email.trim(), password, desktopState ?? undefined);
      if (desktopMode) setDesktopDone(true);
    } catch {
      return;
    }
  };

  if (desktopDone) {
    return (
      <div style={styles.page}>
        <div style={styles.backdropGlow} />
        <div style={styles.doneCard}>
          <div style={styles.doneMark}>✓</div>
          <h2 style={styles.formTitle}>You are signed in</h2>
          <p style={styles.formCopy}>
            You can close this tab and return to the StintSense desktop app.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.backdropGlow} />
      <div style={styles.grid}>
        <section style={styles.brandPanel}>
          <div style={styles.brandTop}>
            <div style={styles.brandBadge}>StintSense</div>
            <h1 style={styles.heroTitle}>Team telemetry, setup history, and race-week decisions in one workspace.</h1>
            <p style={styles.heroCopy}>
              Built for race teams that need clean run context, quick comparisons, and one shared picture of the week.
            </p>
          </div>

          <div style={styles.featureGrid}>
            {[
              ["Shared team view", "Keep every driver, setup, note, and upload tied to the same week workspace."],
              ["Fast comparisons", "Move from overview to driver runs, master rankings, and head-to-head without losing context."],
              ["Real race notes", "Attach setup changes and driver comments directly to each run instead of chasing spreadsheets."],
            ].map(([title, copy]) => (
              <div key={title} style={styles.featureCard}>
                <div style={styles.featureTitle}>{title}</div>
                <div style={styles.featureCopy}>{copy}</div>
              </div>
            ))}
          </div>
        </section>

        <section style={styles.formShell}>
          <div style={styles.formCard}>
            <div style={styles.tabRow}>
              <button
                type="button"
                className={`tab ${mode === "signin" ? "active" : ""}`}
                onClick={() => setMode("signin")}
                style={styles.tabButton}
              >
                Sign In
              </button>
              <button
                type="button"
                className={`tab ${mode === "register" ? "active" : ""}`}
                onClick={() => setMode("register")}
                style={styles.tabButton}
              >
                Register
              </button>
            </div>

            <div style={styles.formHeader}>
              <div className="eyebrow" style={{ marginBottom: "0.75rem" }}>
                Team access
              </div>
              <h2 style={styles.formTitle}>{mode === "signin" ? "Welcome back" : "Create your account"}</h2>
              <p style={styles.formCopy}>{helperCopy}</p>
            </div>

            <form onSubmit={handleSubmit} style={styles.formBody}>
              {mode === "register" && (
                <>
                  <Field label="Invite code">
                    <input
                      className="input"
                      value={inviteCode}
                      onChange={(event) => setInviteCode(event.target.value)}
                      placeholder="Paste your invite code"
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="Full name">
                    <input
                      className="input"
                      value={displayName}
                      onChange={(event) => setDisplayName(event.target.value)}
                      placeholder="How your team should see you"
                      autoComplete="name"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      className="input"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@example.com"
                      autoComplete="email"
                    />
                  </Field>
                  <Field label="iRacing Customer ID">
                    <input
                      className="input input-data"
                      value={iracingCustId}
                      onChange={(event) => setIracingCustId(event.target.value)}
                      placeholder="e.g. 148388"
                      autoComplete="off"
                    />
                  </Field>
                </>
              )}

              {mode === "signin" && (
                <Field label="Email">
                  <input
                    className="input"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </Field>
              )}

              <Field label="Password">
                <input
                  className="input"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={mode === "register" ? "Create a password" : "Enter your password"}
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                />
              </Field>

              {mode === "signin" && (
                <div style={{ textAlign: "right", marginTop: "-0.35rem" }}>
                  <Link
                    to="/forgot-password"
                    style={{ color: "var(--live-strong)", fontSize: "0.85rem", fontWeight: 500, textDecoration: "none" }}
                  >
                    Forgot password?
                  </Link>
                </div>
              )}

              {displayError && (
                <div style={styles.errorBanner}>
                  {displayError}
                </div>
              )}

              <button type="submit" className="btn btn-primary" disabled={loading} style={styles.submitButton}>
                {loading ? (mode === "signin" ? "Signing in…" : "Creating account…") : mode === "signin" ? "Sign in" : "Create account"}
              </button>
            </form>

            <div style={styles.footerNote}>
              {mode === "signin"
                ? "Need access? Ask a team admin for an invite code."
                : "Have an invite code from your team admin? Enter it above."}
            </div>
            <div style={styles.footerNote}>
              New team?{" "}
              <Link to="/signup" style={{ color: "var(--live-strong)", fontWeight: 600, textDecoration: "none" }}>
                Start your free trial →
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={styles.field}>
      <span style={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
    padding: "1.25rem",
    position: "relative",
    overflow: "hidden",
  },
  backdropGlow: {
    display: "none",
  },
  grid: {
    position: "relative",
    zIndex: 1,
    width: "min(1240px, 100%)",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 380px), 1fr))",
    gap: "1.25rem",
    alignItems: "stretch",
  },
  brandPanel: {
    borderRadius: "var(--r-lg)",
    border: "1px solid var(--rule)",
    background: "var(--ink-1)",
    boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.03)",
    padding: "clamp(1.6rem, 4vw, 3rem)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "2rem",
    minHeight: "min(42rem, calc(100vh - 2.5rem))",
  },
  brandTop: {
    display: "flex",
    flexDirection: "column",
    gap: "1.25rem",
  },
  brandBadge: {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    padding: "0.4rem 0.85rem",
    borderRadius: "999px",
    border: "1px solid var(--live-edge)",
    background: "var(--live-soft)",
    color: "var(--live-strong)",
    fontFamily: "var(--font-body)",
    fontSize: "0.8125rem",
    fontWeight: 500,
    letterSpacing: 0,
    textTransform: "none",
  },
  heroTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "clamp(2.5rem, 5vw, 4.4rem)",
    fontWeight: 600,
    letterSpacing: "-0.045em",
    lineHeight: 1.02,
    maxWidth: "14ch",
    color: "var(--text-strong)",
  },
  heroCopy: {
    maxWidth: "42rem",
    color: "var(--text-secondary)",
    fontSize: "1.08rem",
    lineHeight: 1.8,
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "1rem",
  },
  featureCard: {
    minHeight: "100%",
    padding: "1.1rem 1.15rem",
    borderRadius: "var(--r-sm)",
    background: "transparent",
    border: "1px solid var(--rule)",
  },
  featureTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "1rem",
    fontWeight: 600,
    color: "var(--text-strong)",
    marginBottom: "0.45rem",
  },
  featureCopy: {
    color: "var(--text-secondary)",
    fontSize: "0.94rem",
    lineHeight: 1.6,
  },
  formShell: {
    display: "flex",
    alignItems: "stretch",
  },
  formCard: {
    width: "100%",
    borderRadius: "var(--r-md)",
    border: "1px solid var(--rule)",
    background: "var(--ink-2)",
    boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.025)",
    padding: "1.5rem",
    display: "flex",
    flexDirection: "column",
  },
  tabRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "0.5rem",
    marginBottom: "1.5rem",
  },
  tabButton: {
    width: "100%",
  },
  formHeader: {
    marginBottom: "1.25rem",
  },
  formTitle: {
    fontSize: "2rem",
    fontWeight: 700,
    marginBottom: "0.45rem",
  },
  formCopy: {
    color: "var(--text-secondary)",
    fontSize: "0.98rem",
    lineHeight: 1.7,
  },
  formBody: {
    display: "flex",
    flexDirection: "column",
    gap: "0.95rem",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "0.45rem",
  },
  fieldLabel: {
    color: "var(--text-secondary)",
    fontFamily: "var(--font-body)",
    fontSize: "0.85rem",
    fontWeight: 500,
    letterSpacing: 0,
    textTransform: "none",
  },
  errorBanner: {
    padding: "0.85rem 1rem",
    borderRadius: "var(--r-sm)",
    border: "1px solid rgba(248, 113, 113, 0.28)",
    background: "var(--flag-red-soft)",
    color: "var(--flag-red)",
    fontWeight: 600,
  },
  submitButton: {
    width: "100%",
    marginTop: "0.25rem",
  },
  footerNote: {
    marginTop: "1rem",
    paddingTop: "1rem",
    borderTop: "1px solid var(--rule)",
    color: "var(--text-muted)",
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },
  doneCard: {
    position: "relative",
    zIndex: 1,
    margin: "auto",
    maxWidth: "30rem",
    textAlign: "center",
    padding: "2.5rem 2rem",
    borderRadius: "var(--r-md)",
    border: "1px solid var(--rule)",
    background: "var(--ink-2)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "0.75rem",
  },
  doneMark: {
    width: "3.5rem",
    height: "3.5rem",
    borderRadius: "999px",
    display: "grid",
    placeItems: "center",
    fontSize: "1.6rem",
    color: "var(--flag-green)",
    border: "1px solid var(--flag-green)",
    background: "var(--flag-green-soft, rgba(74, 222, 128, 0.12))",
    marginBottom: "0.5rem",
  },
};
